import React, { useState, useEffect, useRef, useMemo } from 'react';
import { IoIosArrowBack, IoIosArrowForward, IoIosPlay } from 'react-icons/io';
import { Skeleton } from '@mui/material';
import Logo from '../../../../assets/images/common/importerr-no-image-logo.png';
import { sanitizeUrl, isAlibabaDetailContext, appendSizeToImageUrl } from './utils';
import { ProductDetailLeftSkeleton } from './ProductDetailSkeletons';
import { TbZoomScan } from "react-icons/tb";
import { usePdpFindSimilar } from './PdpFindSimilar';
import { isAwsUrl } from '../../../../utils/imageUtils';

// Number of thumbnails to show at once
const VISIBLE_THUMBNAILS = 4;

// Add CSS for responsive positioning and video styles
const styles = `
  /* Customer Choice Label */
  .customer-choice-label {
    position: absolute;
    top: 12px;
    left: 0;
    background-color: #000000; /* vibrant orange */
    color: #ff9800;
    padding: 6px 14px;
    font-size: 14px;
    font-weight: 600;
    border-radius: 0 4px 4px 0;
    z-index: 10;
    box-shadow: 0 2px 4px rgba(0,0,0,0.15);
    display: flex;
    align-items: center;
    letter-spacing: 0.5px;
  }

  .pdp-find-similar-icon-btn {
    position: absolute;
    top: 10px;
    height: 34px;
    border-radius: 40px;
    border: 1px solid rgba(37, 99, 235, 0.35); /* blue theme */
    background: rgba(255,255,255,0.98);
    display: flex;
    align-items: center;
    box-shadow: 0 2px 6px rgba(0,0,0,0.12);
    cursor: pointer;
    z-index: 5;
    overflow: hidden;
    white-space: nowrap;
    justify-content: flex-start;
  }

  .pdp-find-similar-icon-btn.expanded {
    padding: 0 12px;
    transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                min-width 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                padding 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .pdp-find-similar-icon-btn.collapsed {
    width: 34px;
    min-width: 34px;
    border-radius: 99px;
    padding: 0;
    justify-content: center;
    transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                min-width 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                padding 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .pdp-find-similar-icon-btn .icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .pdp-find-similar-icon-btn .icon {
    transition: transform 0.3s ease;
    color: #2563eb; /* blue theme */
  }

  .pdp-find-similar-icon-btn:hover .icon {
    transform: scale(1.1);
  }

  .pdp-find-similar-icon-btn .text {
    font-size: 14px;
    font-weight: 800;
    color: #2563eb; /* blue theme */
    overflow: hidden;
    white-space: nowrap;
    flex-shrink: 0;
    margin-left: 8px;
  }

  .pdp-find-similar-icon-btn.expanded .text {
    opacity: 1;
    max-width: 100px;
    transition: opacity 0.2s ease 0.2s,
                max-width 0s ease 0s;
  }

  .pdp-find-similar-icon-btn.collapsed .text {
    opacity: 1;
    max-width: 100px;
    margin-left: 8px;
    transition: none;
  }

  /* Desktop view - 15% from right */
  @media (min-width: 768px) {
    .pdp-find-similar-icon-btn {
      right: 4%;
    }
  }

  /* Mobile view - 10% from right */
  @media (max-width: 767px) {
    .pdp-find-similar-icon-btn {
      right: 4%;
    }
    
    .pdp-find-similar-icon-btn.expanded {
      width: 130px;
      min-width: 130px;
    }
  }

  .product_detail_video {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
  }

  .video_thumbnail {
    position: relative;
    cursor: pointer;
  }

  .video_thumbnail .play_icon_overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.6);
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .video_thumbnail .play_icon_overlay svg {
    color: white;
    font-size: 10px;
    margin-left: 2px;
  }

  /* Thumbnail carousel styles */
  .thumbnail_carousel_wrapper {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  .thumbnail_carousel_container {
    display: flex;
    gap: 0.75rem;
    overflow: hidden;
    flex: 1;
    justify-content: center;
  }

  .thumbnail_carousel_arrow {
    width: 28px;
    height: 28px;
    min-width: 28px;
    border-radius: 50%;
    border: 1px solid #e7e7e7;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #004C81;
    font-size: 16px;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0,0,0,0.08);
    z-index: 5;
    flex-shrink: 0;
  }

  .thumbnail_carousel_arrow:hover {
    background: #f5f5f5;
    border-color: #3DD0E5;
    color: #3DD0E5;
  }

  .thumbnail_carousel_arrow:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .thumbnail_carousel_arrow:disabled:hover {
    background: #ffffff;
    border-color: #e7e7e7;
    color: #004C81;
  }

  /* Mobile: show all thumbnails with horizontal scroll */
  @media (max-width: 767px) {
    .thumbnail_carousel_wrapper {
      gap: 0;
    }
    
    .thumbnail_carousel_arrow {
      display: none;
    }
    
    .thumbnail_carousel_container {
      overflow-x: auto;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    
    .thumbnail_carousel_container::-webkit-scrollbar {
      display: none;
    }
  }
`;

const ProductDetailLeft = ({ product, preferredImage, setPreferredImage, isLoading, isOpenAskInquiry }) => {
  const [carouselStartIndex, setCarouselStartIndex] = useState(0);
  const [mainMediaLoaded, setMainMediaLoaded] = useState(false);
  const [thumbnailsLoaded, setThumbnailsLoaded] = useState({});
  const [videoError, setVideoError] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isFindSimilarExpanded, setIsFindSimilarExpanded] = useState(true);
  const videoRef = useRef(null);
  const expandTimeoutRef = useRef(null);
  const collapseTimeoutRef = useRef(null);

  const handleFindSimilar = usePdpFindSimilar({ product, preferredImage });

  // Get main video URL from product (DB or Alibaba API)
  const mainVideoUrl = product?.mainVideo || product?.detailed_info?.mainVideo || null;
  
  // Get first image from imgUrl array - PRIMARY source for video thumbnail
  const firstImage = product?.imgUrl?.[0] || null;
  
  // Show image by default, not video
  const [showVideo, setShowVideo] = useState(false);
  
  // Video thumbnail source - use imgUrl[0] as primary
  // Priority: imgUrl[0] > fallback
  const videoThumbnailSrc = firstImage;

  // Build the list of all thumbnails (video + images)
  const allThumbnails = useMemo(() => {
    const thumbnails = [];
    
    // Add video thumbnail first if video exists
    if (mainVideoUrl && !videoError) {
      thumbnails.push({ type: 'video', src: videoThumbnailSrc, key: 'video' });
    }
    
    // Add all image thumbnails
    if (product?.imgUrl) {
      product.imgUrl.forEach((img, index) => {
        thumbnails.push({ type: 'image', src: img, key: `img-${index}`, index });
      });
    }
    
    return thumbnails;
  }, [product?.imgUrl, mainVideoUrl, videoError, videoThumbnailSrc]);

  // Calculate total items and whether we need navigation arrows
  const totalThumbnails = allThumbnails.length;
  const needsNavigation = totalThumbnails > VISIBLE_THUMBNAILS;
  const maxStartIndex = Math.max(0, totalThumbnails - VISIBLE_THUMBNAILS);

  // Get visible thumbnails based on current carousel position
  const visibleThumbnails = useMemo(() => {
    return allThumbnails.slice(carouselStartIndex, carouselStartIndex + VISIBLE_THUMBNAILS);
  }, [allThumbnails, carouselStartIndex]);

  // Handle automatic expansion when product loads
  useEffect(() => {
    // Clear any existing timeouts
    if (expandTimeoutRef.current) clearTimeout(expandTimeoutRef.current);
    if (collapseTimeoutRef.current) clearTimeout(collapseTimeoutRef.current);

    // Always keep "Find Similar" visible by default (no hover-only behavior).
    setIsFindSimilarExpanded(true);
    
    return () => {
      if (expandTimeoutRef.current) clearTimeout(expandTimeoutRef.current);
      if (collapseTimeoutRef.current) clearTimeout(collapseTimeoutRef.current);
    };
  }, [product, mainMediaLoaded]);

  // Update showVideo when product changes - show image first by default
  useEffect(() => {
    // Reset video ready state when product changes
    setIsVideoReady(false);
    setImageLoaded(false);
    setVideoError(false);
    
    // Always show image by default, not video
    setShowVideo(false);
    
    // Set preferred image if not already set
    if (!preferredImage || preferredImage === Logo) {
      if (product?.imgUrl?.length > 0) {
        setPreferredImage(product.imgUrl[0]);
      }
    }
    
    // Reset carousel position when product changes
    setCarouselStartIndex(0);
  }, [product, mainVideoUrl]);

  useEffect(() => {
    setMainMediaLoaded(false);
    // Add styles to document head
    const styleElement = document.createElement('style');
    styleElement.innerHTML = styles;
    document.head.appendChild(styleElement);
    
    return () => {
      // Clean up styles when component unmounts
      if (styleElement.parentNode) {
        document.head.removeChild(styleElement);
      }
      // Clear timeouts on unmount
      if (expandTimeoutRef.current) clearTimeout(expandTimeoutRef.current);
      if (collapseTimeoutRef.current) clearTimeout(collapseTimeoutRef.current);
    };
  }, []);

  const handlePrev = () => {
    if (carouselStartIndex > 0) {
      setCarouselStartIndex(prev => Math.max(0, prev - 1));
    }
  };

  const handleNext = () => {
    if (carouselStartIndex < maxStartIndex) {
      setCarouselStartIndex(prev => Math.min(maxStartIndex, prev + 1));
    }
  };

  const handleImage = url => {
    setShowVideo(false);
    setPreferredImage(url);
  };

  const handleVideoClick = () => {
    setShowVideo(true);
    // Reset video ready state when switching to video
    setIsVideoReady(false);
    // Don't reset mainMediaLoaded to avoid layout shift
  };

  const handleVideoError = () => {
    console.log('[ProductDetailLeft] Video failed to load:', mainVideoUrl);
    setVideoError(true);
    setShowVideo(false);
    setIsVideoReady(false);
    // Fallback to first image
    if (product?.imgUrl?.length > 0) {
      setPreferredImage(product.imgUrl[0]);
    }
  };

  if (isLoading) {
    return <ProductDetailLeftSkeleton />;
  }

  // Determine the image to show (with fallback chain)
  const getDisplayImage = () => {
    if (preferredImage && preferredImage !== Logo) {
      return appendSizeToImageUrl(sanitizeUrl(preferredImage), '400x400');
    }
    if (product?.imgUrl?.length > 0) {
      return appendSizeToImageUrl(sanitizeUrl(product.imgUrl[0]), '400x400');
    }
    return Logo;
  };

  return (
    <div className="product_detail_left_section">
      <div className="product_detail_image_box">
        <div className="relative" style={{ maxWidth: '450px', width: '100%', minHeight: '100%' }}>
          
          {/* Customer Choice Label Added Here */}
          {!isOpenAskInquiry && (
            <div className="customer-choice-label">
              <span style={{ color: "white", marginRight: "4px" }}>Customer's</span>Choice
            </div>
          )}

          {!mainMediaLoaded && (
            <Skeleton 
              variant="rectangular" 
              width={450} 
              height={450} 
              animation="wave" 
              className="absolute top-0 left-0"
              style={{ maxWidth: '100%', aspectRatio: '1 / 1', borderRadius: '10px' }}
            />
          )}

          {/* Find Similar icon - PDP (only show for images, not video) */}
          {!showVideo && (
            <button
              type="button"
              onClick={handleFindSimilar}
              title="Find similar products"
              className={`pdp-find-similar-icon-btn ${isFindSimilarExpanded ? 'expanded' : 'collapsed'}`}
            >
              <div className="icon-wrapper">
                <TbZoomScan size={18} className="icon" />
              </div>
              <span className="text">Find Similar</span>
            </button>
          )}

          {/* Main Video Display - Show image first, then switch to video when ready */}
          {showVideo && mainVideoUrl && !videoError ? (
            <div className="product_detail_active_image" style={{
              maxWidth: '450px',
              width: '100%',
              aspectRatio: '1 / 1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
              borderRadius: '10px',
              border: '1px solid #e7e7e7',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {/* Show image as poster while video loads */}
              <img
                src={getDisplayImage()}
                referrerPolicy="no-referrer"
                {...(!isAwsUrl(preferredImage) && { crossOrigin: "anonymous" })}
                onLoad={() => {
                  setImageLoaded(true);
                  setMainMediaLoaded(true);
                }}
                onError={e => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = Logo;
                  setImageLoaded(true);
                  setMainMediaLoaded(true);
                }}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  opacity: isVideoReady ? 0 : 1,
                  transition: 'opacity 0.3s ease-in-out',
                  position: isVideoReady ? 'absolute' : 'relative',
                  zIndex: 1
                }}
                alt={product?.title || 'Product'}
              />
              {/* Video loads in background, shown only when ready */}
              <video
                ref={videoRef}
                className="product_detail_video"
                src={sanitizeUrl(mainVideoUrl)}
                controls
                muted
                loop
                playsInline
                preload="metadata"
                onCanPlay={() => {
                  setIsVideoReady(true);
                  setMainMediaLoaded(true);
                }}
                onLoadedMetadata={() => setMainMediaLoaded(true)}
                onError={handleVideoError}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  opacity: isVideoReady ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out',
                  position: isVideoReady ? 'relative' : 'absolute',
                  zIndex: isVideoReady ? 2 : 0,
                  pointerEvents: isVideoReady ? 'auto' : 'none'
                }}
              />
            </div>
          ) : (
            /* Main Image Display */
            <img
              src={getDisplayImage()}
              className="product_detail_active_image"
              referrerPolicy="no-referrer"
              {...(!isAwsUrl(preferredImage) && { crossOrigin: "anonymous" })}
              onLoad={() => setMainMediaLoaded(true)}
              onError={e => {
                console.log('[ProductDetailLeft] Main image failed to load:', preferredImage);
                e.currentTarget.onerror = null;
                e.currentTarget.src = Logo;
                setMainMediaLoaded(true);
              }}
              style={{ display: mainMediaLoaded ? 'block' : 'none' }}
            />
          )}
        </div>

        {/* Thumbnail Gallery - Carousel */}
        <div className="thumbnail_carousel_wrapper">
          {/* Left Arrow - only show if there are more than VISIBLE_THUMBNAILS and we're not at the start */}
          {needsNavigation && (
            <button 
              className="thumbnail_carousel_arrow"
              onClick={handlePrev}
              disabled={carouselStartIndex === 0}
              aria-label="Previous thumbnails"
            >
              <IoIosArrowBack />
            </button>
          )}

          <div className="thumbnail_carousel_container">
            {visibleThumbnails.map((thumbnail) => {
              if (thumbnail.type === 'video') {
                // Video Thumbnail
                return (
                  <div 
                    key={thumbnail.key}
                    className="relative video_thumbnail"
                    onClick={handleVideoClick}
                    style={{ position: 'relative', cursor: 'pointer' }}
                  >
                    {!thumbnailsLoaded['video'] && (
                      <Skeleton variant="rectangular" width={80} height={80} animation="wave" className="absolute top-0 left-0" />
                    )}
                    <img
                      className="gallery_image"
                      src={(() => {
                        // Use imgUrl[0] as primary source for video thumbnail
                        const thumbSrc = firstImage;
                        if (!thumbSrc) return Logo;
                        // Don't append size to S3/AWS URLs
                        if (isAwsUrl(thumbSrc)) {
                          return sanitizeUrl(thumbSrc);
                        }
                        return appendSizeToImageUrl(sanitizeUrl(thumbSrc), '80x80');
                      })()}
                      referrerPolicy="no-referrer"
                      {...(!(isAwsUrl(firstImage)) && { crossOrigin: "anonymous" })}
                      onLoad={() => setThumbnailsLoaded(prev => ({ ...prev, video: true }))}
                      onError={e => {
                        console.log('[ProductDetailLeft] Video thumbnail failed to load, trying fallback');
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = Logo;
                        setThumbnailsLoaded(prev => ({ ...prev, video: true }));
                      }}
                      style={{ 
                        display: thumbnailsLoaded['video'] ? 'block' : 'none',
                        border: showVideo ? '2px solid #3DD0E5' : 'none',
                        width: '80px',
                        height: '80px',
                        objectFit: 'cover'
                      }}
                      alt="Video thumbnail"
                    />
                    <div className="play_icon_overlay" style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      background: 'rgba(0, 0, 0, 0.6)',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none'
                    }}>
                      <IoIosPlay style={{ color: 'white', fontSize: '12px', marginLeft: '2px' }} />
                    </div>
                  </div>
                );
              } else {
                // Image Thumbnail
                const img = thumbnail.src;
                const index = thumbnail.index;
                const baseUrl = sanitizeUrl(img);
                const thumbUrl = isAlibabaDetailContext() || isAwsUrl(img) ? baseUrl : appendSizeToImageUrl(baseUrl, '80x80');
                const isThumbLoaded = thumbnailsLoaded[index];
                const isSelected = !showVideo && preferredImage === baseUrl;

                return (
                  <div key={thumbnail.key} className="relative">
                    {!isThumbLoaded && <Skeleton variant="rectangular" width={80} height={80} animation="wave" className="absolute top-0 left-0" />}
                    <img
                      className="gallery_image"
                      src={thumbUrl}
                      referrerPolicy="no-referrer"
                      {...(!isAwsUrl(img) && { crossOrigin: "anonymous" })}
                      onLoad={() => setThumbnailsLoaded(prev => ({ ...prev, [index]: true }))}
                      onError={e => {
                        console.log('[ProductDetailLeft] Thumbnail failed to load:', img);
                        e.currentTarget.style.display = 'none';
                        setThumbnailsLoaded(prev => ({ ...prev, [index]: true }));
                      }}
                      onClick={() => handleImage(baseUrl)}
                      onMouseOver={() => handleImage(baseUrl)}
                      style={{ 
                        display: isThumbLoaded ? 'block' : 'none',
                        border: isSelected ? '2px solid #3DD0E5' : 'none'
                      }}
                    />
                  </div>
                );
              }
            })}
          </div>

          {/* Right Arrow - only show if there are more than VISIBLE_THUMBNAILS and we're not at the end */}
          {needsNavigation && (
            <button 
              className="thumbnail_carousel_arrow"
              onClick={handleNext}
              disabled={carouselStartIndex >= maxStartIndex}
              aria-label="Next thumbnails"
            >
              <IoIosArrowForward />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailLeft;
